/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { Calendar } from '@services/requests/housien-models/Calendar';

/**
 * How the signed-in user's Google link is behaving right now.
 *
 * `readonly` is the tail of the verification work: connections granted while
 * Housien only imported events carry a read-only scope, so the write half of
 * the settings has to stay shut for them until they authorise again.
 */
export type GoogleConnectionState =
  | 'disconnected'
  | 'connected'
  | 'readonly'
  | 'error';

/** Scope fragment that tells a read-only grant from a read/write one. */
const READONLY_SCOPE = 'calendar.events.readonly';

/** Sync statuses that mean the link is there but not usable. */
const BROKEN_STATUSES = ['error', 'revoked', 'invalid_grant'];

/**
 * The Google-synced calendar belonging to the signed-in user, if any.
 *
 * The Google link is per-user: a calendar a teammate synced must never read
 * as "connected" here, or the user is offered Refresh and Disconnect for
 * credentials that are not theirs.
 *
 * @param calendars - Calendars visible to the user.
 * @param userEmail - Email of the signed-in user.
 * @returns The user's own Google-synced calendar, or undefined.
 */
export const ownGoogleCalendar = (
  calendars: Calendar[],
  userEmail: string,
): Calendar | undefined =>
  calendars.find((c) => Boolean(c.sync?.google) && c.owner_id === userEmail);

/**
 * The Google account behind the user's own link.
 *
 * @param calendar - The user's Google-synced calendar, if any.
 * @returns The Google account email, or undefined when the backend did not
 *   record it.
 */
export const googleAccountEmail = (calendar?: Calendar): string | undefined =>
  calendar?.sync?.google?.google_account_email;

/**
 * Classifies the user's Google link.
 *
 * Order matters: a broken link is reported as broken even when its scope is
 * read-only, because reconnecting is the only action that helps.
 *
 * @param calendar - The user's Google-synced calendar, if any.
 * @returns The state the settings UI should present.
 */
export const googleConnectionState = (
  calendar?: Calendar,
): GoogleConnectionState => {
  const google = calendar?.sync?.google;
  if (!google) return 'disconnected';
  if (BROKEN_STATUSES.includes(String(google.sync_status ?? ''))) return 'error';
  if (String(google.scope ?? '').includes(READONLY_SCOPE)) return 'readonly';
  return 'connected';
};

/**
 * Google calendars imported into Housien, as seen from the calendar list.
 *
 * @param calendars - Calendars visible to the user.
 * @param userEmail - Email of the signed-in user.
 * @returns The user's imported Google calendars.
 */
export const importedGoogleCalendars = (
  calendars: Calendar[],
  userEmail: string,
): Calendar[] =>
  calendars.filter((c) => Boolean(c.sync?.google) && c.owner_id === userEmail);
